import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { SportIcon } from "@/components/SportIcon";
import { SaveButton } from "@/components/SaveButton";
import { ShareButtons } from "@/components/ShareButtons";
import api, { API } from "@/lib/api";
import { longDate, sportName, distanceList, REG_STATUS } from "@/lib/helpers";
import { MapPin, CalendarDays, Building2, Clock, ExternalLink, Share2, CalendarPlus, Gift, Medal, Shirt, Package, ChevronLeft, ChevronRight, Loader2, ArrowRight, X, Mail, MessageCircle, Globe } from "lucide-react";
import NotFound from "./NotFound";

function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={22} className="text-[#06b6d4]" />
        <h2 className="font-display font-extrabold uppercase tracking-wide text-2xl text-[#0f172a]">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default function RaceDetail() {
  const { slug } = useParams();
  const [race, setRace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true); setNotFound(false);
    api.get(`/races/${slug}`).then(({ data }) => setRace(data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <Layout><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" size={36} /></div></Layout>;
  if (notFound || !race) return <NotFound />;
  return <Layout><RaceDetailBody race={race} /></Layout>;
}

export function RaceDetailBody({ race, preview = false }) {
  const [gi, setGi] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  const register = () => {
    if (!race.registration_url) return;
    if (!preview) api.post("/track", { event_type: "register_click", race_id: race.id }).catch(() => {});
    window.open(race.registration_url, "_blank", "noopener");
  };
  const officialUrl = race.official_website ? (/^https?:\/\//i.test(race.official_website) ? race.official_website : `https://${race.official_website}`) : "";

  const distances = [...new Set(race.distances || [])];
  const reg = REG_STATUS[race.registration_status] || REG_STATUS.coming_soon;
  const h = race.highlights || {};
  const medalImgs = h.medal?.images?.length ? h.medal.images : (h.medal?.image_url ? [h.medal.image_url] : []);
  const tshirtImgs = h.tshirt?.images?.length ? h.tshirt.images : (h.tshirt?.image_url ? [h.tshirt.image_url] : []);
  const hasMerch = medalImgs.length || tshirtImgs.length || h.race_pack?.description;
  const gallery = race.gallery || [];

  return (
    <>
      {/* HERO */}
      {(() => {
        const mode = race.banner_text_display || "full";
        if (mode === "none") {
          return (
            <div className="relative bg-[#0a1420] min-h-[340px] sm:min-h-[360px] overflow-hidden" data-testid="hero-no-overlay">
              <img src={race.hero_image} alt={race.name} className="absolute inset-0 w-full h-full object-cover" />
            </div>
          );
        }
        return (
          <div className="relative bg-[#0a1420] min-h-[340px] sm:min-h-[360px] flex items-center overflow-hidden">
            <img src={race.hero_image} alt={race.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(90deg, rgba(10,20,32,0.62) 0%, rgba(10,20,32,0.30) 18%, rgba(10,20,32,0) 35%)" }} />
            <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 [text-shadow:0_1px_6px_rgba(0,0,0,0.45)]">
              {race.is_demo && <span className="inline-block mb-3 bg-[#f59e0b] text-[#0a1420] text-xs font-black uppercase tracking-widest px-3 py-1 rounded" data-testid="demo-event-badge">Demo Event</span>}
              <div>
                <span className="inline-flex items-center gap-1.5 bg-navy-card/80 border border-slate-700 text-white text-sm font-semibold px-3 py-1 rounded-full">
                  <SportIcon sport={race.sport} size={14} className="text-[#22d3ee]" />{sportName(race.sport)}
                </span>
                <h1 className="font-display font-black uppercase text-4xl sm:text-5xl text-white leading-none mt-4 max-w-3xl" data-testid="race-title">{race.name}</h1>
                {mode === "full" && (
                  <>
                    <div className="mt-5 space-y-2 text-white font-medium">
                      <div className="flex items-center gap-2"><CalendarDays size={18} className="text-[#22d3ee]" />{longDate(race.date)}</div>
                      <div className="flex items-center gap-2"><MapPin size={18} className="text-[#22d3ee]" />{race.city}, {race.country}</div>
                      <div className="flex items-center gap-2"><Building2 size={18} className="text-[#22d3ee]" />{race.venue}</div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-5">
                      {distances.map((d) => <span key={d} className="font-mono-data text-base sm:text-lg font-bold bg-navy-card/80 border border-slate-700 text-white px-4 py-1.5 rounded-lg">{d}</span>)}
                    </div>
                  </>
                )}
                {race.is_demo && <p className="mt-4 text-xs text-amber-300 bg-amber-900/30 inline-block px-3 py-1 rounded">Sample race — for demonstration only</p>}
              </div>
            </div>
          </div>
        );
      })()}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-3 gap-10">
        {/* MAIN */}
        <div className="lg:col-span-2 space-y-12">
          <Section id="overview" icon={MapPin} title="About This Race">
            <div className="text-slate-600 leading-relaxed whitespace-pre-line">{race.description_full || race.description_short}</div>
          </Section>

          {race.additional_info && (
            <Section id="additional-info" icon={Clock} title="Additional Information">
              <div className="text-slate-600 leading-relaxed whitespace-pre-line" data-testid="additional-info-content">{race.additional_info}</div>
            </Section>
          )}

          {distances.length > 0 && (
            <Section id="distances" icon={ArrowRight} title="Race Distances">
              <div className="flex flex-wrap gap-2.5" data-testid="race-distances-list">
                {distances.map((d) => (
                  <span key={d} className="font-mono-data text-base font-bold bg-[#0a1420] text-white px-4 py-2 rounded-lg">{d}</span>
                ))}
              </div>
            </Section>
          )}

          {(race.categories || []).length > 0 && (
            <Section id="categories" icon={Clock} title="Race Categories">
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm" data-testid="categories-table">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
                    <tr><th className="text-left p-4 font-semibold">Category</th><th className="text-left p-4 font-semibold">Distance</th><th className="text-right p-4 font-semibold">Fee</th><th className="text-right p-4 font-semibold">Start</th></tr>
                  </thead>
                  <tbody>
                    {race.categories.map((c, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-4 font-semibold text-slate-800">{c.name}</td>
                        <td className="p-4 font-mono-data text-slate-700">{c.distance}</td>
                        <td className="p-4 text-right font-mono-data text-slate-800">RM{c.price}</td>
                        <td className="p-4 text-right text-slate-600">{c.start_time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {(() => {
            const pps = (race.pricing_periods || []).map((p) => p.rows ? p : ({ label: p.label, start_date: p.start_date, end_date: p.end_date, rows: (p.price !== "" && p.price != null) ? [{ distance: p.distance || "", price: p.price }] : [] })).filter((p) => (p.rows || []).length > 0);
            return pps.length > 0 && (
              <Section id="pricing" icon={Clock} title="Registration Pricing">
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm" data-testid="pricing-periods-table">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
                      <tr><th className="text-left p-4 font-semibold">Registration Period</th><th className="text-left p-4 font-semibold">Distance</th><th className="text-right p-4 font-semibold">Fee</th><th className="text-right p-4 font-semibold">Period</th></tr>
                    </thead>
                    <tbody>
                      {pps.map((p, pi) => (p.rows || []).map((r, ri) => (
                        <tr key={`${pi}-${ri}`} className="border-t border-slate-100">
                          <td className="p-4 font-semibold text-slate-800">{ri === 0 ? p.label : ""}</td>
                          <td className="p-4 font-mono-data text-slate-700">{r.distance || "—"}</td>
                          <td className="p-4 text-right font-mono-data text-slate-800">RM{r.price}</td>
                          <td className="p-4 text-right text-slate-600">{ri === 0 ? ([p.start_date, p.end_date].filter(Boolean).join(" – ") || "—") : ""}</td>
                        </tr>
                      )))}
                    </tbody>
                  </table>
                </div>
              </Section>
            );
          })()}

          {hasMerch && (
            <Section id="merchandise" icon={Gift} title="Medals, T-Shirts & Race Pack">
              <div className="space-y-8" data-testid="merchandise-section">
                {medalImgs.length > 0 && (
                  <MerchSubSection icon={Medal} label="Finisher Medals" testid="medals">
                    {h.medal?.description && <p className="text-sm text-slate-600 mb-4 -mt-1">{h.medal.description}</p>}
                    <PhotoGrid images={medalImgs} alt="medal" onOpen={setLightbox} />
                  </MerchSubSection>
                )}
                {tshirtImgs.length > 0 && (
                  <MerchSubSection icon={Shirt} label="Event T-Shirts" testid="tshirts">
                    {h.tshirt?.description && <p className="text-sm text-slate-600 mb-4 -mt-1">{h.tshirt.description}</p>}
                    <PhotoGrid images={tshirtImgs} alt="tshirt" onOpen={setLightbox} />
                  </MerchSubSection>
                )}
                {h.race_pack?.description && (
                  <MerchSubSection icon={Package} label="Race Pack Information" testid="race-pack">
                    <div className="bg-slate-50 rounded-xl p-5 text-sm text-slate-600 leading-relaxed whitespace-pre-line" data-testid="race-pack-info">{h.race_pack.description}</div>
                  </MerchSubSection>
                )}
              </div>
            </Section>
          )}

          {gallery.length > 0 && (
            <Section id="gallery" icon={Package} title="Photo Gallery">
              <div className="relative rounded-xl overflow-hidden bg-slate-100">
                <img src={gallery[gi]} alt="" onClick={() => setLightbox(gallery[gi])} className="w-full max-h-[28rem] object-contain cursor-zoom-in" />
                {gallery.length > 1 && (
                  <>
                    <button onClick={() => setGi((gi - 1 + gallery.length) % gallery.length)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow"><ChevronLeft size={20} /></button>
                    <button onClick={() => setGi((gi + 1) % gallery.length)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow"><ChevronRight size={20} /></button>
                  </>
                )}
              </div>
              {gallery.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
                  {gallery.map((g, i) => <button key={i} onClick={() => setGi(i)} className={`shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 ${i === gi ? "border-[#22d3ee]" : "border-transparent"}`}><img src={g} alt="" className="w-full h-full object-cover" /></button>)}
                </div>
              )}
            </Section>
          )}

        </div>

        {/* SIDEBAR */}
        <div className="space-y-4">
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center gap-2 text-sm font-semibold mb-3" style={{ color: reg.color }}>
                <span className="w-2 h-2 rounded-full" style={{ background: reg.color }} />{reg.label}
                {race.registration_deadline && <span className="text-slate-400 font-normal ml-auto">Closes {race.registration_deadline}</span>}
              </div>
              {race.registration_url ? (
                <>
                  <button onClick={register} data-testid="register-now-cta-button"
                    className="w-full bg-[#22d3ee] text-[#0a1420] font-bold py-3.5 rounded-lg hover:bg-[#06b6d4] transition-colors flex items-center justify-center gap-2 uppercase tracking-wide">
                    Register Now <ExternalLink size={18} />
                  </button>
                  {race.is_demo && <p className="text-center text-sm font-semibold text-slate-500 mt-1" data-testid="register-demo-note">(Demo)</p>}
                  <p className="text-center text-xs text-slate-400 mt-2">Registration handled on the organizer's website</p>
                </>
              ) : (
                <p className="text-center text-sm text-slate-500" data-testid="no-registration-note">Registration link not available yet</p>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
              <SaveButton race={race} variant="button" className="!w-full !border-0 !rounded-none !justify-start px-5 py-4" />
              <a href={`${API}/races/${race.id}/ics`} data-testid="add-to-calendar-button" className="flex items-center gap-2 px-5 py-4 font-semibold text-slate-700 hover:bg-slate-50">
                <CalendarPlus size={18} className="text-[#06b6d4]" /> Add to Calendar
              </a>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3"><Share2 size={18} className="text-[#06b6d4]" /><h4 className="font-display font-bold uppercase text-sm text-slate-800">Share Race</h4></div>
              <ShareButtons race={race} layout="grid" />
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3"><Building2 size={18} className="text-[#06b6d4]" /><h4 className="font-display font-bold uppercase text-sm text-slate-800">Organizer</h4></div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#0a1420] text-[#22d3ee] grid place-items-center font-display font-black overflow-hidden shrink-0">
                  {race.organizer_logo ? <img src={race.organizer_logo} alt="" className="w-full h-full object-cover" /> : race.organizer_name?.[0]}
                </div>
                <div>
                  <div className="font-display font-bold uppercase text-slate-800 leading-tight">{race.organizer_name}</div>
                  <div className="text-xs text-slate-500">Race organizer</div>
                  {officialUrl && (
                    <a
                      href={officialUrl}
                      target="_blank" rel="noreferrer noopener"
                      data-testid="official-website-link"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#06b6d4] mt-1.5 hover:underline"
                    >
                      <Globe size={13} /> Official Race Website <ExternalLink size={11} />
                    </a>
                  )}
                  {(race.contact_whatsapp || race.contact_email) && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {race.contact_whatsapp && (
                        <a
                          href={`https://wa.me/${(race.contact_whatsapp || "").replace(/[^0-9]/g, "")}`}
                          target="_blank" rel="noreferrer"
                          data-testid="whatsapp-organizer-button"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#128C3E] border border-[#25D366]/50 rounded-md px-2.5 py-1 hover:bg-[#25D366] hover:text-white transition-colors"
                        >
                          <MessageCircle size={13} /> WhatsApp Organizer
                        </a>
                      )}
                      {race.contact_email && (
                        <a
                          href={`mailto:${race.contact_email}?subject=${encodeURIComponent("Enquiry about " + race.name)}`}
                          data-testid="email-organizer-button"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#06b6d4] border border-[#06b6d4]/40 rounded-md px-2.5 py-1 hover:bg-[#06b6d4] hover:text-white transition-colors"
                        >
                          <Mail size={13} /> Email Organizer
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h4 className="font-display font-bold uppercase text-sm text-slate-800 mb-1">Location</h4>
              <p className="text-slate-700">{race.venue}</p>
              <p className="text-sm text-slate-500">{race.city}, {race.state}, {race.country}</p>
              <a href={`https://www.google.com/maps/search/${encodeURIComponent(race.venue + " " + race.city)}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[#06b6d4] font-semibold text-sm mt-3 hover:underline">View on Google Maps <ExternalLink size={14} /></a>
            </div>
          </div>
        </div>
      </div>

      {/* STICKY MOBILE CTA */}
      <div className="lg:hidden sticky bottom-0 z-40 bg-white border-t border-slate-200 p-3 flex gap-2">
        <SaveButton race={race} variant="button" className="!py-3" />
        {race.registration_url && <button onClick={register} data-testid="mobile-register-cta" className="flex-1 bg-[#22d3ee] text-[#0a1420] font-bold py-3 rounded-lg flex items-center justify-center gap-2 uppercase text-sm">Register Now <ExternalLink size={16} /></button>}
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} data-testid="image-lightbox" className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4 cursor-zoom-out">
          <img src={lightbox} alt="" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 bg-white/90 rounded-full p-2"><X size={22} /></button>
        </div>
      )}
    </>
  );
}

function MerchSubSection({ icon: Icon, label, testid, children }) {
  return (
    <div data-testid={`merch-subsection-${testid}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} className="text-[#06b6d4]" />
        <h3 className="font-display font-bold uppercase tracking-wide text-lg text-[#0f172a]">{label}</h3>
      </div>
      {children}
    </div>
  );
}

function PhotoGrid({ images, alt, onOpen }) {
  return (
    <div className="flex flex-wrap justify-center gap-5" data-testid={`photo-grid-${alt}`}>
      {images.map((img, i) => (
        <button
          key={i}
          onClick={() => onOpen(img)}
          data-testid={`photo-${alt}-${i}`}
          className="basis-[calc(50%-0.625rem)] sm:basis-[calc(33.333%-0.834rem)] grow-0 shrink-0 bg-white border border-slate-200 rounded-xl overflow-hidden cursor-zoom-in hover:shadow-md hover:border-[#22d3ee] transition-all"
        >
          <div className="aspect-[4/3] bg-slate-50 p-3">
            <img src={img} alt={`${alt} ${i + 1}`} className="w-full h-full object-contain" />
          </div>
        </button>
      ))}
    </div>
  );
}
